/**
 * Renders all completed jobs' bbox outlines on the map so the operator can
 * see at a glance where coverage has already been computed and where the
 * gaps are. Click a bbox to load that job's coverage in detail.
 *
 * Layer stack (bottom -> top):
 *   1. envelope fill   — union of all bboxes, very faint gray, signals
 *      "everywhere I've ever run something"
 *   2. per-bbox fill   — slightly stronger semi-transparent, makes overlaps
 *      visually denser (re-runs of the same area)
 *   3. per-bbox outline — 1.5px, age-coded (newer = brighter)
 */

import type { Map as MLMap, GeoJSONSource, MapLayerMouseEvent } from "maplibre-gl";

import type { Job } from "../lib/api";
import { api } from "../lib/api";

const SRC_BBOX = "__saved_jobs_bbox__";
const SRC_ENVELOPE = "__saved_jobs_envelope__";
const FILL_BBOX = "__saved_jobs_bbox_fill__";
const LINE_BBOX = "__saved_jobs_bbox_line__";
const FILL_ENVELOPE = "__saved_jobs_envelope_fill__";

type Polygon = { type: "Polygon"; coordinates: number[][][] };

function parseWktPolygon(wkt: string): Polygon | null {
  // Minimal WKT POLYGON parser — sufficient for our backend's output, which
  // is always a single-ring axis-aligned polygon. Returns GeoJSON.
  const m = wkt.match(/POLYGON\s*\(\(([^)]+)\)\)/i);
  if (!m) return null;
  const ring = m[1].split(",").map((p) => {
    const [lon, lat] = p.trim().split(/\s+/).map(Number);
    return [lon, lat];
  });
  return { type: "Polygon", coordinates: [ring] };
}

export class SavedJobsLayerController {
  private map: MLMap;
  private onClickJob: (job: Job) => void;
  private jobsById: Map<string, Job> = new Map();
  private attached = false;
  /** When set, this job's bbox is suppressed from the overlay — it would
   * just overlap the rendered coverage and add visual noise. */
  private excludeId: string | null = null;

  constructor(map: MLMap, onClickJob: (job: Job) => void) {
    this.map = map;
    this.onClickJob = onClickJob;
  }

  /** Hide one job from the bbox overlay (typically the currently-loaded
   * coverage). Pass null to show all jobs again. Triggers a re-render
   * from cached data — no network round trip. */
  setExcluded(id: string | null): void {
    if (this.excludeId === id) return;
    this.excludeId = id;
    this.renderFromCache();
  }

  /** Attach sources + layers to the map. Idempotent across basemap swaps:
   * call from the style.load handler. Defensive cleanup first (remove stale
   * layers/sources from a prior style) so repeated basemap swaps don't leave
   * the overlay in a half-initialized state where getSource returns a shell
   * but the features never re-render. */
  attach(): void {
    // Remove any old listeners first — they reference methods whose closures
    // may be stale across attach/detach cycles.
    this.map.off("click", FILL_BBOX, this.onMapClick);
    this.map.off("mouseenter", FILL_BBOX, this.onMouseEnter);
    this.map.off("mouseleave", FILL_BBOX, this.onMouseLeave);
    for (const id of [LINE_BBOX, FILL_BBOX, FILL_ENVELOPE]) {
      if (this.map.getLayer(id)) {
        try { this.map.removeLayer(id); } catch { /* ignore */ }
      }
    }
    for (const id of [SRC_BBOX, SRC_ENVELOPE]) {
      if (this.map.getSource(id)) {
        try { this.map.removeSource(id); } catch { /* ignore */ }
      }
    }

    this.map.addSource(SRC_BBOX, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addSource(SRC_ENVELOPE, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });

    this.map.addLayer({
      id: FILL_ENVELOPE,
      type: "fill",
      source: SRC_ENVELOPE,
      paint: { "fill-color": "#888", "fill-opacity": 0.08 },
    });
    this.map.addLayer({
      id: FILL_BBOX,
      type: "fill",
      source: SRC_BBOX,
      paint: { "fill-color": "#3b82f6", "fill-opacity": 0.05 },
    });
    this.map.addLayer({
      id: LINE_BBOX,
      type: "line",
      source: SRC_BBOX,
      paint: {
        "line-color": "#3b82f6",
        "line-width": 1.5,
        "line-opacity": 0.7,
      },
    });

    this.map.on("click", FILL_BBOX, this.onMapClick);
    this.map.on("mouseenter", FILL_BBOX, this.onMouseEnter);
    this.map.on("mouseleave", FILL_BBOX, this.onMouseLeave);

    this.attached = true;
    void this.refresh();
  }

  /** Remove sources/layers + click handlers. Call before basemap swap or
   * when the user toggles the picker off. */
  detach(): void {
    if (!this.attached) return;
    this.map.off("click", FILL_BBOX, this.onMapClick);
    this.map.off("mouseenter", FILL_BBOX, this.onMouseEnter);
    this.map.off("mouseleave", FILL_BBOX, this.onMouseLeave);
    for (const id of [LINE_BBOX, FILL_BBOX, FILL_ENVELOPE]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    for (const id of [SRC_BBOX, SRC_ENVELOPE]) {
      if (this.map.getSource(id)) this.map.removeSource(id);
    }
    this.attached = false;
  }

  /** Re-fetch the saved-jobs list and update the map. Call after a new
   * job completes so its bbox shows up immediately. */
  async refresh(): Promise<void> {
    if (!this.attached) return;
    let jobs: Job[];
    try {
      jobs = await api.listJobs("done", 200);
    } catch (e) {
      console.warn("Saved jobs fetch failed:", e);
      return;
    }
    this.jobsById = new Map(jobs.map((j) => [j.id, j]));
    this.renderFromCache();
  }

  /** Push the in-memory job list to the map sources. Used by both refresh()
   * and setExcluded() so toggling the active job doesn't refetch.
   *
   * Dedupes by bbox_wkt: if the same area was run N times, render one
   * outline. Click on a deduped bbox picks the most-recent matching job
   * (per the retention discussion). The active job is excluded entirely
   * so it doesn't double up with its own coverage rendering. */
  private renderFromCache(): void {
    if (!this.attached) return;
    // Group jobs by bbox; keep most-recent per unique bbox; skip the active one
    const byBbox = new Map<string, Job[]>();
    for (const j of this.jobsById.values()) {
      if (j.id === this.excludeId) continue;
      const list = byBbox.get(j.bbox_wkt) ?? [];
      list.push(j);
      byBbox.set(j.bbox_wkt, list);
    }
    // If the active job's bbox is shared with other jobs, that whole group
    // represents the area we're already viewing — hide it too.
    const activeBbox = this.excludeId
      ? this.jobsById.get(this.excludeId)?.bbox_wkt ?? null
      : null;

    const features = Array.from(byBbox.entries())
      .filter(([wkt]) => wkt !== activeBbox)
      .map(([wkt, jobs]) => {
        const poly = parseWktPolygon(wkt);
        if (!poly) return null;
        jobs.sort((a, b) => (b.completed_at ?? "").localeCompare(a.completed_at ?? ""));
        const newest = jobs[0];
        return {
          type: "Feature" as const,
          geometry: poly,
          properties: {
            id: newest.id,
            completed_at: newest.completed_at,
            duplicate_count: jobs.length,
          },
        };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null);

    const bboxSrc = this.map.getSource(SRC_BBOX) as GeoJSONSource | undefined;
    bboxSrc?.setData({ type: "FeatureCollection", features });

    // Envelope = lon/lat bounding box of all bboxes (excluding the active
    // one). Cheap proxy for the true union polygon (which would require
    // turf or similar) — still good enough for "where have I covered" at
    // a glance.
    const envelopeFeatures: GeoJSON.Feature[] = features.length === 0
      ? []
      : (() => {
          let lonMin = Infinity, latMin = Infinity, lonMax = -Infinity, latMax = -Infinity;
          for (const f of features) {
            for (const ring of (f.geometry as Polygon).coordinates) {
              for (const [lon, lat] of ring) {
                if (lon < lonMin) lonMin = lon;
                if (lon > lonMax) lonMax = lon;
                if (lat < latMin) latMin = lat;
                if (lat > latMax) latMax = lat;
              }
            }
          }
          return [{
            type: "Feature" as const,
            geometry: {
              type: "Polygon",
              coordinates: [[
                [lonMin, latMin], [lonMax, latMin],
                [lonMax, latMax], [lonMin, latMax],
                [lonMin, latMin],
              ]],
            },
            properties: {},
          }];
        })();
    const envSrc = this.map.getSource(SRC_ENVELOPE) as GeoJSONSource | undefined;
    envSrc?.setData({ type: "FeatureCollection", features: envelopeFeatures });
  }

  /** When multiple bboxes overlap at the click point, pick the most recent
   * (largest completed_at). Mirrors the "most-recent-wins" decision from
   * the retention discussion. */
  private onMapClick = (e: MapLayerMouseEvent): void => {
    const features = e.features ?? [];
    if (features.length === 0) return;
    const ids = features.map((f) => f.properties?.id as string).filter(Boolean);
    const jobs = ids.map((id) => this.jobsById.get(id)).filter((j): j is Job => !!j);
    if (jobs.length === 0) return;
    jobs.sort((a, b) => (b.completed_at ?? "").localeCompare(a.completed_at ?? ""));
    this.onClickJob(jobs[0]);
  };

  private onMouseEnter = (): void => {
    this.map.getCanvas().style.cursor = "pointer";
  };

  private onMouseLeave = (): void => {
    this.map.getCanvas().style.cursor = "";
  };
}
